// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentEyebrow — section-label chrome for assessment-flow screens
 * @version 0.2.0
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import styles from './AssessmentEyebrow.module.css';

/*
 * AssessmentEyebrow — section-label chrome for assessment-flow screens.
 *
 *   <AssessmentEyebrow label="Introduction" />
 *
 * Renders a small uppercase label above a short teal accent rule. The rule
 * carries a soft halo via box-shadow that lifts it off the surrounding dark
 * surface. Pairs above a section heading (h1/h2) on the assessment screens;
 * the heading owns the actual semantic level.
 */

export interface AssessmentEyebrowProps extends HTMLAttributes<HTMLDivElement> {
  /** Section label text. Rendered with `text-transform: uppercase` regardless
   *  of source casing. */
  label: string;
  /**
   * Colour treatment. `dark` (default) is the muted-grey label + soft-teal rule
   * for dark surfaces; `light` is navy-ish label + teal-accessible rule for the
   * light-surface flow. A per-instance variant, not a runtime theme mode.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentEyebrow = forwardRef<HTMLDivElement, AssessmentEyebrowProps>(
  function AssessmentEyebrow({ label, className, theme = 'dark', as, ...rest }, ref) {
  const Component = as ?? 'div';
  const rootClass = [styles.root, theme === 'light' ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <Component ref={ref} className={rootClass} {...rest}>
      <span className={styles.label}>{label}</span>
      <span className={styles.rule} aria-hidden="true" />
    </Component>
  );
  },
);

AssessmentEyebrow.displayName = 'AssessmentEyebrow';
